import { z } from 'zod'

// Single object schema (not discriminated union) - the union form trips up the
// react-hook-form Control generic when paired with FormField. Identity rules are
// enforced via .superRefine so PERSON requires firstName/lastName and COMPANY
// requires name; the other side stays optional.

const customerCoreShape = {
  type: z.enum(['PERSON', 'COMPANY']),
  // Person identity (required when type=PERSON, validated below)
  firstName: z.string().trim().max(128).optional(),
  lastName: z.string().trim().max(128).optional(),
  pesel: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^\d{11}$/.test(v), { message: 'PESEL musi mieć 11 cyfr' }),
  // Company identity (required when type=COMPANY, validated below)
  name: z.string().trim().max(255).optional(),
  nip: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^\d{10}$/.test(v), { message: 'NIP musi mieć 10 cyfr' }),
  regon: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^(\d{9}|\d{14})$/.test(v), {
      message: 'REGON musi mieć 9 lub 14 cyfr',
    }),
  // Common
  typeProjectCode: z.string().trim().max(32).optional(),
  voivodeshipCode: z.string().trim().max(32).optional(),
  assigneeUserId: z
    .string()
    .optional()
    .refine((v) => !v || /^\d+$/.test(v), { message: 'Tylko cyfry' }),
  note: z.string().trim().max(2000).optional(),
}

function refineIdentityRequired<T extends z.ZodObject<typeof customerCoreShape | (typeof customerCoreShape & { isActive: z.ZodBoolean })>>(
  schema: T,
) {
  return schema.superRefine((data, ctx) => {
    if (data.type === 'PERSON') {
      if (!data.firstName?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Imię jest wymagane', path: ['firstName'] })
      }
      if (!data.lastName?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Nazwisko jest wymagane', path: ['lastName'] })
      }
    } else if (data.type === 'COMPANY') {
      if (!data.name?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Nazwa firmy jest wymagana', path: ['name'] })
      }
    }
  })
}

export const createCustomerSchema = refineIdentityRequired(z.object(customerCoreShape))
export type CreateCustomerFormValues = z.input<typeof createCustomerSchema>
export type CreateCustomerFormParsed = z.output<typeof createCustomerSchema>

export const updateCustomerCoreSchema = refineIdentityRequired(
  z.object({ ...customerCoreShape, isActive: z.boolean() }),
)
export type UpdateCustomerCoreFormValues = z.input<typeof updateCustomerCoreSchema>
export type UpdateCustomerCoreFormParsed = z.output<typeof updateCustomerCoreSchema>

export const phoneSchema = z.object({
  value: z.string().trim().min(9, 'Numer musi mieć min. 9 cyfr'),
  label: z.string().trim().max(64).optional(),
  isPrimary: z.boolean(),
})
export type PhoneFormValues = z.input<typeof phoneSchema>

export const emailSchema = z.object({
  value: z
    .string()
    .trim()
    .regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'Niepoprawny format email'),
  label: z.string().trim().max(64).optional(),
  isPrimary: z.boolean(),
})
export type EmailFormValues = z.input<typeof emailSchema>

export const addressSchema = z
  .object({
    kind: z.enum(['residence', 'correspondence', 'installation']),
    street: z.string().trim().max(255).optional(),
    city: z.string().trim().max(128).optional(),
    postalCode: z.string().trim().optional(),
    countryCode: z
      .string()
      .trim()
      .regex(/^[A-Z]{2}$/, 'Kod kraju musi być 2-literowy ISO (np. PL)')
      .default('PL'),
    isPrimary: z.boolean(),
  })
  .refine((d) => Boolean(d.street || d.city || d.postalCode), {
    message: 'Wypełnij ulicę, miasto lub kod pocztowy',
    path: ['street'],
  })
  .refine(
    (d) => d.countryCode !== 'PL' || !d.postalCode || /^\d{2}-\d{3}$/.test(d.postalCode),
    { message: 'Kod pocztowy PL musi mieć format NN-NNN', path: ['postalCode'] },
  )
export type AddressFormValues = z.input<typeof addressSchema>
